import { useEffect, useMemo } from 'react'
import { salsaAnalytics } from '../services/salsaAnalytics'

/**
 * Homepage hero A/B test wired into Salsa's GrowthBook-based experiment system.
 *
 * Follows the canonical Salsa assignment engine semantics
 * (ExecutiveOS crates/exec-daemon/src/services/growthbook.rs):
 * deterministic hash of `seed:key:sticky_key` -> bucket in [0,1) -> coverage
 * gate -> weighted variant. Assignment is sticky per anonymous visitor and
 * reported to Salsa analytics (team ONZOE) as experiment_viewed /
 * experiment_conversion events for the experiments.salsas.io dashboards.
 * Query params (`?hero=animated|static`) are test overrides only.
 */

export const HERO_EXPERIMENT = {
  key: 'homepage_hero_fleet_scenes',
  name: 'Homepage hero: static vs animated fleet scenes',
  seed: 'onzoe-homepage-hero-2026-07',
  coverage: 1,
  hashAttribute: 'anonymous_id',
  platform: 'salsa_growthbook',
  growthbookHost: 'https://experiments.salsas.io',
  variants: [
    { key: 'static', label: 'Static hero (control)', weight: 0.5 },
    { key: 'animated', label: 'Animated fleet scenes', weight: 0.5 },
  ],
} as const

export type HeroVariantKey = (typeof HERO_EXPERIMENT.variants)[number]['key']

export interface HeroAssignment {
  experimentKey: string
  variantKey: HeroVariantKey
  variantIndex: number
  bucket: number
  coverage: number
  hashAttribute: string
  stickyKey: string
  reason: string
}

const VISITOR_ID_STORAGE_KEY = 'onzoe_ab_visitor_id'

const getVisitorId = (): string => {
  try {
    const existing = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY)
    if (existing) return existing

    const generated =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, generated)
    return generated
  } catch {
    return 'anonymous'
  }
}

/** FNV-1a 32-bit hash mapped to [0, 1), mirroring the deterministic bucketing contract. */
const bucketFor = (seed: string, experimentKey: string, stickyKey: string): number => {
  const input = `${seed}:${experimentKey}:${stickyKey}`
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0) / 0x100000000
}

const chooseVariant = (bucket: number): { index: number; key: HeroVariantKey } => {
  const totalWeight = HERO_EXPERIMENT.variants.reduce((sum, v) => sum + v.weight, 0)
  let cursor = 0
  for (let index = 0; index < HERO_EXPERIMENT.variants.length; index += 1) {
    cursor += HERO_EXPERIMENT.variants[index].weight / totalWeight
    if (bucket < cursor) {
      return { index, key: HERO_EXPERIMENT.variants[index].key }
    }
  }
  const last = HERO_EXPERIMENT.variants.length - 1
  return { index: last, key: HERO_EXPERIMENT.variants[last].key }
}

const readQueryOverride = (): HeroVariantKey | null => {
  try {
    const value = new URLSearchParams(window.location.search).get('hero')
    if (value === 'animated' || value === 'static') return value
  } catch {
    // ignore malformed URLs
  }
  return null
}

export const getHeroAssignment = (): HeroAssignment => {
  const override = readQueryOverride()
  const stickyKey = getVisitorId()

  if (override) {
    const index = HERO_EXPERIMENT.variants.findIndex((v) => v.key === override)
    return {
      experimentKey: HERO_EXPERIMENT.key,
      variantKey: override,
      variantIndex: index,
      bucket: -1,
      coverage: HERO_EXPERIMENT.coverage,
      hashAttribute: HERO_EXPERIMENT.hashAttribute,
      stickyKey,
      reason: 'query_override',
    }
  }

  const bucket = bucketFor(HERO_EXPERIMENT.seed, HERO_EXPERIMENT.key, stickyKey)
  const normalized = bucket >= HERO_EXPERIMENT.coverage ? 0 : bucket / HERO_EXPERIMENT.coverage
  const { index, key } = chooseVariant(normalized)

  return {
    experimentKey: HERO_EXPERIMENT.key,
    variantKey: key,
    variantIndex: index,
    bucket,
    coverage: HERO_EXPERIMENT.coverage,
    hashAttribute: HERO_EXPERIMENT.hashAttribute,
    stickyKey,
    reason: `bucketed by ${HERO_EXPERIMENT.hashAttribute} using deterministic hash`,
  }
}

const assignmentProperties = (assignment: HeroAssignment) => ({
  experiment_key: assignment.experimentKey,
  experiment_name: HERO_EXPERIMENT.name,
  variant: assignment.variantKey,
  variant_key: assignment.variantKey,
  variant_index: assignment.variantIndex,
  bucket: assignment.bucket,
  coverage: assignment.coverage,
  hash_attribute: assignment.hashAttribute,
  assignment_mode: 'local_deterministic',
  assignment_reason: assignment.reason,
  experiment_platform: HERO_EXPERIMENT.platform,
  growthbook_host: HERO_EXPERIMENT.growthbookHost,
})

export const trackHeroConversion = (assignment: HeroAssignment, goal: string) => {
  salsaAnalytics.track('experiment_conversion', {
    ...assignmentProperties(assignment),
    goal,
  })
}

let exposureTracked = false

export const useHeroExperiment = (): HeroAssignment => {
  const assignment = useMemo(getHeroAssignment, [])

  useEffect(() => {
    if (exposureTracked) return
    exposureTracked = true
    salsaAnalytics.track('experiment_viewed', assignmentProperties(assignment))
  }, [assignment])

  return assignment
}
